<script setup lang="ts">
  // Components
  import { DefaultTable, TableHeader, SearchNoResults, HeaderTableWithButtons } from '@/shared/ui';

  // Interfaces
  import type { Component } from 'vue';
  import type { DocumentRevision } from '@/entities/Document';
  import type { OrderOptions } from '@/shared/lib';

  export interface SearchTableColumn {
    text: string;
    thClass?: string;
  }

  defineProps<{
    revisions: DocumentRevision[];
    headerText: string;
    columns: SearchTableColumn[];
    rowComponent: Component;
    orderOptions?: OrderOptions[];
    isFilterActive?: boolean;
    tableClass?: string;
    colWidths?: string[];
  }>();

  const emit = defineEmits<{
    (e: 'update:sort', sortCamp: string, orderFlow: string): void;
    (e: 'filter-clicked'): void;
  }>();

  const handleSortOptions = (sortCamp: string, orderFlow: string) => {
    emit('update:sort', sortCamp, orderFlow);
  };

  const handleFilterClicked = () => {
    emit('filter-clicked');
  };
</script>

<template>
  <DefaultTable
    :table-class="tableClass"
    class="divide-y divide-gray-02"
  >
    <colgroup v-if="colWidths">
      <col
        v-for="width in colWidths"
        :key="width"
        :class="width"
      >
    </colgroup>
    <thead>
      <tr>
        <HeaderTableWithButtons
          id="header-table-with-buttons"
          :colspan="columns.length"
          :text="headerText"
          :order-options="orderOptions"
          :is-filter-active="isFilterActive"
          @update:sort="handleSortOptions"
          @filter-clicked="handleFilterClicked"
        />
      </tr>
      <tr>
        <TableHeader
          v-for="column in columns"
          :key="column.text"
          :text="column.text"
          :th-class="column.thClass"
          class="text-xs/5"
        />
      </tr>
    </thead>
    <tbody class="bg-white-01 divide-y divide-gray-02">
      <tr v-if="!revisions.length">
        <td
          :colspan="columns.length"
          class="py-8"
        >
          <SearchNoResults />
        </td>
      </tr>
      <component
        :is="rowComponent"
        v-for="revision in revisions"
        :key="revision.id"
        :revision="revision"
      />
    </tbody>
  </DefaultTable>
</template>
